import type { JSX } from 'react';
import { motion } from 'framer-motion';
import { revealUp, inViewOnce, useRevealGate } from './anim';
import { RoomVoice } from './RoomVoice';
import { SectionGlow } from './SectionGlow';

/**
 * "What is Duck Duck Space" — a short, warm explanation paired with a supporting visual that SHOWS the
 * signature mechanic instead of leaving a blank navy void (P0-6). Two columns on desktop: the copy, and a
 * {@link RoomVoice} illustration where two ducks stand together in a room, both heard at the same clear
 * volume — the page demonstrating its single best idea (room voice) at a glance.
 */
export function WhatIsSection(): JSX.Element {
  const reduce = useRevealGate();
  return (
    <section
      aria-labelledby="what-heading"
      className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24"
    >
      <SectionGlow tone="warm" side="left" />
      <motion.div
        variants={reduce ? undefined : revealUp}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14"
      >
        <div className="text-center lg:text-left">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-300/80">A place, not an app</p>
          <h2 id="what-heading" className="mt-4 text-balance text-3xl font-bold tracking-tight text-white sm:text-5xl">
            A real place for your people — that lives in your browser.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-white/65 sm:text-xl lg:mx-0">
            Create a beautiful virtual space — an office, a café, a cinema hall — pick your character, and walk in. Step into a room and
            everyone there hears you — clear and equal, no dial-in — screens share with the people beside
            you, meetings just happen where you stand. It feels less like a call and more like being in
            the same place.
          </p>
        </div>

        {/* The signature mechanic, shown not told: two ducks in a room, both heard at the same clear volume. */}
        <RoomVoice />
      </motion.div>
    </section>
  );
}
